import React, { createContext, useContext, useState, useEffect } from "react";
import { userAPI } from "../services/api";

const PreferencesContext = createContext();

const DICTIONARY = {
  English: {
    dashboard: "Dashboard",
    journey: "My Journey",
    library: "Training Library",
    liveSessions: "Live Sessions",
    dietPlan: "Diet Plan",
    goals: "Wellness Goals",
    achievements: "Achievements",
    notifications: "Notifications",
    settings: "Settings",
    profile: "Profile",
    logOut: "Log Out",
    welcomeBack: "Welcome back",
    dailyStreak: "Daily Streak",
    activeSubscription: "Active Subscription",
    upgradePlan: "Upgrade Plan",
    theme: "Theme",
    language: "App Language",
    timezone: "Timezone",
    currency: "Currency",
    dateFormat: "Date Format",
    measurement: "Measurement Units"
  },
  Hindi: {
    dashboard: "डैशबोर्ड",
    journey: "मेरी यात्रा",
    library: "प्रशिक्षण लाइब्रेरी",
    liveSessions: "लाइव सत्र",
    dietPlan: "आहार योजना",
    goals: "स्वास्थ्य लक्ष्य",
    achievements: "उपलब्धियां",
    notifications: "सूचनाएं",
    settings: "सेटिंग्स",
    profile: "प्रोफ़ाइल",
    logOut: "लॉग आउट",
    welcomeBack: "पुनः स्वागत है",
    dailyStreak: "दैनिक क्रम",
    activeSubscription: "सक्रिय सदस्यता",
    upgradePlan: "प्लान अपग्रेड करें",
    theme: "थीम",
    language: "ऐप की भाषा",
    timezone: "समय क्षेत्र",
    currency: "मुद्रा",
    dateFormat: "तिथि प्रारूप",
    measurement: "माप इकाइयाँ"
  },
  Spanish: {
    dashboard: "Panel de control",
    journey: "Mi Viaje",
    library: "Biblioteca de Entrenamiento",
    liveSessions: "Sesiones en Vivo",
    dietPlan: "Plan de Dieta",
    goals: "Metas de Bienestar",
    achievements: "Logros",
    notifications: "Notificaciones",
    settings: "Ajustes",
    profile: "Perfil",
    logOut: "Cerrar Sesión",
    welcomeBack: "Bienvenido de nuevo",
    dailyStreak: "Racha Diaria",
    activeSubscription: "Suscripción Activa",
    upgradePlan: "Mejorar Plan",
    theme: "Tema",
    language: "Idioma de la App",
    timezone: "Zona Horaria",
    currency: "Moneda",
    dateFormat: "Formato de Fecha",
    measurement: "Unidades de Medida"
  },
  French: {
    dashboard: "Tableau de bord",
    journey: "Mon Parcours",
    library: "Bibliothèque de Cours",
    liveSessions: "Sessions en Direct",
    dietPlan: "Plan Alimentaire",
    goals: "Objectifs Bien-être",
    achievements: "Succès",
    notifications: "Notifications",
    settings: "Paramètres",
    profile: "Profil",
    logOut: "Déconnexion",
    welcomeBack: "Bon retour",
    dailyStreak: "Série Quotidienne",
    activeSubscription: "Abonnement Actif",
    upgradePlan: "Changer de Forfait",
    theme: "Thème",
    language: "Langue de l'application",
    timezone: "Fuseau Horaire",
    currency: "Devise",
    dateFormat: "Format de Date",
    measurement: "Unités de Mesure"
  },
  German: {
    dashboard: "Dashboard",
    journey: "Meine Reise",
    library: "Trainingsbibliothek",
    liveSessions: "Live-Sitzungen",
    dietPlan: "Ernährungsplan",
    goals: "Wohlfühlziele",
    achievements: "Erfolge",
    notifications: "Benachrichtigungen",
    settings: "Einstellungen",
    profile: "Profil",
    logOut: "Abmelden",
    welcomeBack: "Willkommen zurück",
    dailyStreak: "Täglicher Streak",
    activeSubscription: "Aktives Abonnement",
    upgradePlan: "Tarif Upgraden",
    theme: "Thema",
    language: "App-Sprache",
    timezone: "Zeitzone",
    currency: "Währung",
    dateFormat: "Datumsformat",
    measurement: "Masseinheiten"
  }
};

export const PreferencesProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => localStorage.getItem("divyayoga_theme") || "light");
  const [language, setLanguageState] = useState(() => localStorage.getItem("divyayoga_lang") || "English");
  const [timezone, setTimezoneState] = useState("Asia/Kolkata (IST +5:30)");
  const [dateFormat, setDateFormatState] = useState("DD/MM/YYYY");
  const [currency, setCurrencyState] = useState("INR — ₹");
  const [measurement, setMeasurementState] = useState("Metric (kg, cm)");

  // Sync theme with DOM documentElement class
  useEffect(() => {
    localStorage.setItem("divyayoga_theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  // Sync language with localStorage
  useEffect(() => {
    localStorage.setItem("divyayoga_lang", language);
  }, [language]);

  // Fetch profile from backend to restore DB preferences on login/mount
  useEffect(() => {
    const token = localStorage.getItem("divyayoga_token");
    if (!token) return;

    userAPI.getProfile()
      .then(res => {
        if (res.data?.data) {
          const p = res.data.data;
          if (p.appLanguage) {
            const cleanLang = p.appLanguage.replace(/[^A-Za-z]/g, "").trim();
            if (DICTIONARY[cleanLang]) setLanguageState(cleanLang);
            else setLanguageState(p.appLanguage);
          }
          if (p.timezone) setTimezoneState(p.timezone);
          if (p.dateFormat) setDateFormatState(p.dateFormat);
          if (p.currency) setCurrencyState(p.currency);
          if (p.measurement) setMeasurementState(p.measurement);
        }
      })
      .catch(() => null);
  }, []);

  const updatePreferences = async (newPrefs) => {
    if (newPrefs.theme) setThemeState(newPrefs.theme);
    if (newPrefs.language) setLanguageState(newPrefs.language);
    if (newPrefs.timezone) setTimezoneState(newPrefs.timezone);
    if (newPrefs.dateFormat) setDateFormatState(newPrefs.dateFormat);
    if (newPrefs.currency) setCurrencyState(newPrefs.currency);
    if (newPrefs.measurement) setMeasurementState(newPrefs.measurement);

    try {
      await userAPI.updateProfile({
        appLanguage: newPrefs.language || language,
        timezone: newPrefs.timezone || timezone,
        dateFormat: newPrefs.dateFormat || dateFormat,
        currency: newPrefs.currency || currency,
        measurement: newPrefs.measurement || measurement,
      });
    } catch (e) {
      console.warn("Could not save preferences", e);
    }
  };

  // Translation Helper Function t(key)
  const t = (key) => {
    const langKey = Object.keys(DICTIONARY).find(k => language.includes(k)) || "English";
    const dict = DICTIONARY[langKey] || DICTIONARY.English;
    return dict[key] || DICTIONARY.English[key] || key;
  };

  // Date Formatter Helper
  const formatDate = (dateInput) => {
    if (!dateInput) return "";
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return String(dateInput);

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    if (dateFormat.includes("MM/DD")) return `${mm}/${dd}/${yyyy}`;
    if (dateFormat.includes("YYYY-MM")) return `${yyyy}-${mm}-${dd}`;
    return `${dd}/${mm}/${yyyy}`; // Default DD/MM/YYYY
  };

  // Currency Formatter Helper
  const formatCurrency = (amountInINR) => {
    const num = Number(amountInINR) || 0;
    if (currency.includes("USD") || currency.includes("$")) {
      return `$${(num / 83.5).toFixed(2)}`;
    }
    if (currency.includes("EUR") || currency.includes("€")) {
      return `€${(num / 90.2).toFixed(2)}`;
    }
    return `₹${num.toLocaleString("en-IN")}`;
  };

  // Measurement Weight Formatter Helper
  const formatWeight = (weightKg) => {
    const val = Number(weightKg) || 0;
    if (measurement.includes("Imperial") || measurement.includes("lbs")) {
      return `${Math.round(val * 2.20462)} lbs`;
    }
    return `${val} kg`;
  };

  // Measurement Height Formatter Helper
  const formatHeight = (heightCm) => {
    const val = Number(heightCm) || 0;
    if (measurement.includes("Imperial") || measurement.includes("ft")) {
      const totalInches = val / 2.54;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return `${feet} ft ${inches} in`;
    }
    return `${val} cm`;
  };

  return (
    <PreferencesContext.Provider
      value={{
        theme,
        setTheme: (t) => updatePreferences({ theme: t }),
        language,
        setLanguage: (l) => updatePreferences({ language: l }),
        timezone,
        dateFormat,
        currency,
        measurement,
        updatePreferences,
        t,
        formatDate,
        formatCurrency,
        formatWeight,
        formatHeight
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    return {
      theme: "light",
      setTheme: () => {},
      language: "English",
      setLanguage: () => {},
      timezone: "Asia/Kolkata (IST +5:30)",
      dateFormat: "DD/MM/YYYY",
      currency: "INR — ₹",
      measurement: "Metric (kg, cm)",
      updatePreferences: async () => {},
      t: (k) => k,
      formatDate: (d) => String(d),
      formatCurrency: (a) => `₹${a}`,
      formatWeight: (w) => `${w} kg`,
      formatHeight: (h) => `${h} cm`
    };
  }
  return context;
};
